import React, { useEffect, useState } from 'react';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Building2, ChevronDown, ChevronUp, ExternalLink, Loader2, Mail, Search, Users } from 'lucide-react';
import { useAdmin } from '../context/AdminContext';
import AdminNav from './AdminNav';

/*
 * Enterprise inquiries: the "Contact sales" form of the Enterprise plan on the website pricing page.
 * GET /api/admin/enterprise-inquiries (list + counts), PATCH /api/admin/enterprise-inquiries/:id (status, notes).
 */

const STATUSES = [
  { key: 'all', label: 'All' },
  { key: 'new', label: 'New' },
  { key: 'contacted', label: 'Contacted' },
  { key: 'closed', label: 'Closed' },
];

const STATUS_STYLE = {
  new: 'bg-sky-500/10 text-sky-300 border-sky-500/20',
  contacted: 'bg-amber-500/10 text-amber-300 border-amber-500/20',
  closed: 'bg-slate-500/10 text-slate-400 border-white/5',
};

const VOLUME_LABELS = {
  under_10k: 'Under 10,000',
  '10k_100k': '10,000 – 100,000',
  '100k_1m': '100,000 – 1 million',
  over_1m: 'Over 1 million',
  not_sure: 'Not sure yet',
};

function formatDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? '—'
    : d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function inviteHref(invite) {
  if (!invite) return null;
  return /^https?:\/\//i.test(invite) ? invite : `https://${invite}`;
}

function StatusBadge({ status }) {
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider border ${STATUS_STYLE[status] || STATUS_STYLE.closed}`}>
      {status}
    </span>
  );
}

/** The expanded part of a row: full message, contact details, status and notes. */
function InquiryDetails({ inquiry, canEdit, onSave }) {
  const [notes, setNotes] = useState(inquiry.notes || '');
  const [saving, setSaving] = useState(null); // 'status' | 'notes' | null
  const [error, setError] = useState(null);

  const save = async (patch, what) => {
    setSaving(what);
    setError(null);
    try {
      await onSave(inquiry.id, patch);
    } catch (e) {
      setError(e.message || 'Could not save');
    } finally {
      setSaving(null);
    }
  };

  const invite = inviteHref(inquiry.discordInvite);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 px-6 pb-6 pt-1">
      <div className="lg:col-span-2 space-y-4">
        <div>
          <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2">Message</p>
          <p className="text-sm text-slate-200 whitespace-pre-wrap break-words leading-relaxed bg-slate-950/50 border border-white/5 rounded-xl p-4">
            {inquiry.message}
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
          <div className="bg-slate-950/40 border border-white/5 rounded-xl p-3">
            <span className="text-slate-500 block mb-1">Email</span>
            <a href={`mailto:${inquiry.email}?subject=${encodeURIComponent('Ryzz Enterprise')}`} className="text-indigo-300 hover:text-indigo-200 font-semibold break-all inline-flex items-center gap-1.5">
              <Mail className="w-3.5 h-3.5 shrink-0" /> {inquiry.email}
            </a>
          </div>
          <div className="bg-slate-950/40 border border-white/5 rounded-xl p-3">
            <span className="text-slate-500 block mb-1">Discord server invite</span>
            {invite ? (
              <a href={invite} target="_blank" rel="noopener noreferrer" className="text-indigo-300 hover:text-indigo-200 font-semibold break-all inline-flex items-center gap-1.5">
                {inquiry.discordInvite} <ExternalLink className="w-3 h-3 shrink-0" />
              </a>
            ) : (
              <span className="text-slate-500">—</span>
            )}
          </div>
          <div className="bg-slate-950/40 border border-white/5 rounded-xl p-3">
            <span className="text-slate-500 block mb-1">Server members</span>
            <span className="text-slate-200 font-mono">{inquiry.memberCount != null ? inquiry.memberCount.toLocaleString() : '—'}</span>
          </div>
          <div className="bg-slate-950/40 border border-white/5 rounded-xl p-3">
            <span className="text-slate-500 block mb-1">Translations / month</span>
            <span className="text-slate-200">{VOLUME_LABELS[inquiry.translationsPerMonth] || '—'}</span>
          </div>
          {inquiry.discordUserId && (
            <div className="bg-slate-950/40 border border-white/5 rounded-xl p-3 sm:col-span-2">
              <span className="text-slate-500 block mb-1">Logged-in Ryzz user</span>
              <Link to={`/user/${inquiry.discordUserId}`} className="text-indigo-300 hover:text-indigo-200 font-mono">{inquiry.discordUserId}</Link>
            </div>
          )}
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2">Status</p>
          <div className="flex gap-1.5">
            {['new', 'contacted', 'closed'].map((s) => (
              <button
                key={s}
                disabled={!canEdit || saving !== null || inquiry.status === s}
                onClick={() => save({ status: s }, 'status')}
                className={`flex-1 px-2 py-2 rounded-lg text-[11px] font-bold capitalize border transition-all ${inquiry.status === s
                  ? STATUS_STYLE[s]
                  : 'bg-slate-900/40 border-white/5 text-slate-400 hover:text-white hover:border-indigo-500/30 disabled:hover:text-slate-400 disabled:hover:border-white/5'
                  } disabled:cursor-not-allowed`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        <div>
          <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2">Internal notes</p>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={!canEdit}
            maxLength={2000}
            rows={4}
            placeholder="e.g. Called on Oct 18, sending a quote for 3 servers"
            className="w-full bg-slate-950/50 border border-white/5 rounded-xl p-3 text-xs text-slate-200 placeholder:text-slate-600 focus:outline-none focus:border-indigo-500/40 resize-y"
          />
          {canEdit && (
            <button
              onClick={() => save({ notes }, 'notes')}
              disabled={saving !== null || notes === (inquiry.notes || '')}
              className="mt-2 w-full py-2 rounded-lg text-xs font-bold bg-indigo-500/15 border border-indigo-500/30 text-indigo-200 hover:bg-indigo-500/25 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
            >
              {saving === 'notes' && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              Save notes
            </button>
          )}
        </div>

        {error && <p className="text-xs text-rose-400">{error}</p>}
        <p className="text-[10px] text-slate-600">
          Received {formatDateTime(inquiry.createdAt)}
          {inquiry.handledBy && ` · last updated by ${inquiry.handledBy}`}
        </p>
      </div>
    </div>
  );
}

export default function EnterpriseInquiriesPage() {
  const { API_BASE, apiFetch, can } = useAdmin();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState('all');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState(null);
  const canEdit = can('enterprise');

  // Debounce the search box.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const queryKey = ['admin-enterprise-inquiries', status, search, page];
  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page), limit: '25' });
      if (status !== 'all') params.set('status', status);
      if (search) params.set('search', search);
      const res = await apiFetch(`${API_BASE}/enterprise-inquiries?${params.toString()}`);
      if (!res) throw new Error('Session expired');
      if (!res.ok) throw new Error(res.status === 403 ? "You don't have access to Enterprise inquiries." : 'Failed to load inquiries');
      return res.json();
    },
    placeholderData: keepPreviousData,
  });
  const data = query.data;

  const saveInquiry = async (id, patch) => {
    const res = await apiFetch(`${API_BASE}/enterprise-inquiries/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    });
    if (!res) throw new Error('Session expired');
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(body.error || 'Could not save');
    await queryClient.invalidateQueries({ queryKey: ['admin-enterprise-inquiries'] });
  };

  const counts = data?.counts || {};
  const inquiries = data?.inquiries || [];

  return (
    <div className="min-h-screen bg-[#070a13] text-slate-200 flex flex-col font-sans">
      <AdminNav />

      <main className="flex-1 max-w-7xl w-full mx-auto p-6 space-y-6">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <h1 className="text-2xl font-black text-white tracking-tight flex items-center gap-2">
              <Building2 className="w-6 h-6 text-sky-400" /> Enterprise inquiries
            </h1>
            <p className="text-xs text-slate-400 mt-1">
              Requests sent with the "Contact sales" form of the Enterprise plan on the pricing page. Newest first.
            </p>
          </div>
          <div className="relative w-full md:w-72">
            <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search name, email, invite, message"
              className="w-full bg-slate-900/60 border border-white/5 rounded-xl pl-9 pr-3 py-2.5 text-xs text-slate-200 placeholder:text-slate-500 focus:outline-none focus:border-indigo-500/40"
            />
          </div>
        </div>

        <div className="flex gap-1 bg-slate-950/50 p-1 rounded-xl border border-white/5 w-fit">
          {STATUSES.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => {
                setStatus(key);
                setPage(1);
              }}
              className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all ${status === key ? 'bg-indigo-500/20 text-indigo-200' : 'text-slate-500 hover:text-slate-300'}`}
            >
              {label}
              <span className="ml-1.5 text-[10px] font-mono text-slate-500">{counts[key] ?? 0}</span>
            </button>
          ))}
        </div>

        <div className="glass-panel rounded-2xl border-white/[0.05] overflow-hidden">
          {query.isLoading ? (
            <div className="py-16 flex flex-col items-center text-slate-500 text-xs">
              <Loader2 className="w-6 h-6 animate-spin text-indigo-400 mb-2" /> Loading inquiries...
            </div>
          ) : query.isError ? (
            <div className="py-16 text-center text-xs text-rose-400">{query.error.message}</div>
          ) : inquiries.length === 0 ? (
            <div className="py-16 text-center">
              <Building2 className="w-10 h-10 text-slate-700 mx-auto mb-2" />
              <p className="text-sm font-bold text-slate-400">No inquiries {status !== 'all' ? `marked "${status}"` : 'yet'}</p>
              <p className="text-xs text-slate-500 mt-1">They appear here as soon as someone sends the Enterprise form.</p>
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {inquiries.map((inq) => {
                const open = openId === inq.id;
                return (
                  <div key={inq.id} className={open ? 'bg-white/[0.015]' : ''}>
                    <button
                      onClick={() => setOpenId(open ? null : inq.id)}
                      className="w-full text-left px-6 py-4 grid grid-cols-12 gap-3 items-center hover:bg-white/[0.02] transition-colors"
                    >
                      <div className="col-span-12 md:col-span-4 min-w-0">
                        <span className="text-sm font-bold text-white block truncate">{inq.firstName} {inq.lastName}</span>
                        <span className="text-[11px] text-slate-400 block truncate">{inq.email}</span>
                      </div>
                      <div className="col-span-6 md:col-span-3 text-xs text-slate-300 truncate">
                        <span className="inline-flex items-center gap-1.5">
                          <Users className="w-3.5 h-3.5 text-slate-500" />
                          {inq.memberCount != null ? `${inq.memberCount.toLocaleString()} members` : 'Members: —'}
                        </span>
                        <span className="block text-[10px] text-slate-500 mt-0.5 truncate">
                          {VOLUME_LABELS[inq.translationsPerMonth] || 'Volume not given'}
                        </span>
                      </div>
                      <div className="col-span-6 md:col-span-3 text-[11px] text-slate-400">{formatDateTime(inq.createdAt)}</div>
                      <div className="col-span-12 md:col-span-2 flex items-center justify-between md:justify-end gap-3">
                        <StatusBadge status={inq.status} />
                        {open ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
                      </div>
                    </button>
                    {open && <InquiryDetails inquiry={inq} canEdit={canEdit} onSave={saveInquiry} />}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {data?.totalPages > 1 && (
          <div className="flex items-center justify-center gap-3 text-xs">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="px-3 py-1.5 rounded-lg border border-white/5 bg-slate-900/40 text-slate-300 disabled:opacity-40"
            >
              Previous
            </button>
            <span className="text-slate-500">Page {page} of {data.totalPages}</span>
            <button
              onClick={() => setPage((p) => Math.min(data.totalPages, p + 1))}
              disabled={page >= data.totalPages}
              className="px-3 py-1.5 rounded-lg border border-white/5 bg-slate-900/40 text-slate-300 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        )}
      </main>
    </div>
  );
}
